
import { useState } from "react"
import { Plus } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import type { Database } from "@/integrations/supabase/types"

type Category = Database["public"]["Tables"]["client_categories"]["Row"]
type FieldType = "text" | "textarea" | "radio" | "select" | "date"

const FIELD_TYPES_WITH_OPTIONS: FieldType[] = ["radio", "select"]

interface NewField {
  categoryId: string
  name: string
  type: FieldType
  required: boolean
  options: string
}

interface FieldFormNewProps {
  categories: Category[] | undefined
  onSubmit: (field: NewField) => Promise<void>
}

export function FieldFormNew({ categories, onSubmit }: FieldFormNewProps) {
  const [newField, setNewField] = useState<NewField>({
    categoryId: "",
    name: "",
    type: "text" as FieldType,
    required: false,
    options: "",
  })

  const handleSubmit = async () => {
    await onSubmit(newField)
    setNewField({
      categoryId: "",
      name: "",
      type: "text" as FieldType,
      required: false,
      options: "",
    })
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Add New Field</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4">
          <div className="grid gap-2">
            <Label htmlFor="categorySelect">Category</Label>
            <Select
              value={newField.categoryId}
              onValueChange={(value) =>
                setNewField((prev) => ({ ...prev, categoryId: value }))
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                {categories?.map((category) => (
                  <SelectItem key={category.id} value={category.id}>
                    {category.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="fieldName">Field Name</Label>
            <Input
              id="fieldName"
              value={newField.name}
              onChange={(e) =>
                setNewField((prev) => ({ ...prev, name: e.target.value }))
              }
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="fieldType">Field Type</Label>
            <Select
              value={newField.type}
              onValueChange={(value: FieldType) =>
                setNewField((prev) => ({ ...prev, type: value }))
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="text">Text</SelectItem>
                <SelectItem value="textarea">Text Area</SelectItem>
                <SelectItem value="radio">Radio</SelectItem>
                <SelectItem value="select">Select</SelectItem>
                <SelectItem value="date">Date</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {FIELD_TYPES_WITH_OPTIONS.includes(newField.type) && (
            <div className="grid gap-2">
              <Label htmlFor="fieldOptions">Options (comma-separated)</Label>
              <Input
                id="fieldOptions"
                value={newField.options}
                onChange={(e) =>
                  setNewField((prev) => ({
                    ...prev,
                    options: e.target.value,
                  }))
                }
                placeholder="Option 1, Option 2, Option 3"
              />
            </div>
          )}
        </div>
        <Button onClick={handleSubmit}>
          <Plus className="mr-2 h-4 w-4" />
          Add Field
        </Button>
      </CardContent>
    </Card>
  )
}
